import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {
  Container,
  Title,
  InputWrapper,
  InputField,
  ClearButton,
  AppliedCode,
  ApplyButton,
  ErrorMessage,
} from "./DiscountCodeComponent.styled";
import { CircularProgress } from "@mui/material";

/**
 * Component for applying discount codes to subscription changes
 */
const DiscountCodeComponent = ({
  onApplyCode,
  initialCode = "",
  placeholder = "Add Coupon here",
  showTitle = true,
  error = "",
  loading = false,
  discountInfo = null,
}) => {
  const [discountCode, setDiscountCode] = useState(initialCode);

  // // Reset input when discount is removed externally
  // useEffect(() => {
  //   // Only reset the discount code when discountInfo is explicitly removed
  //   // AND initialCode is empty AND discountCode had a previous value
  //   if (!discountInfo && initialCode === "" && discountCode !== "") {
  //     setDiscountCode("");
  //   }

  //   // When initialCode has a value, update the discountCode
  //   if (initialCode !== "") {
  //     setDiscountCode(initialCode);
  //   }
  // }, [discountInfo, initialCode]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setDiscountCode(value);
    
    // Clear error when input becomes empty
    if (!value.trim() && onApplyCode) {
      onApplyCode("");
    }
  };

  const handleKeyPress = (e) => {
    // Apply code on enter key
    if (e.key === "Enter" && e.target.value.trim()) {
      applyCode(e.target.value);
    }
  };

  const clearInput = () => {
    setDiscountCode("");
    
    // Notify parent to clear error when input is manually cleared
    if (onApplyCode) {
      onApplyCode("");
    }
  };

  const applyCode = (code) => {
    if (!code.trim()) return;

    // Call the onApplyCode handler passed from parent
    if (onApplyCode) {
      onApplyCode(code);
    }
  };

  const removeAppliedCode = () => {
    // Reset local state
    setDiscountCode("");

    // Call parent handler with null to indicate removal
    if (onApplyCode) {
      onApplyCode(null);
    }
  };

  // Format discount percentage to be displayed
  const getDiscountText = () => {
    if (!discountInfo) return "";

    // If discount info has percentOff, show it
    if (discountInfo.percentOff) {
      return `${discountInfo.percentOff}% off`;
    }

    // Calculate percentage based on original and discounted prices
    if (discountInfo.originalPrice && discountInfo.discountedPrice) {
      const original = parseFloat(discountInfo.originalPrice);
      const discounted = parseFloat(discountInfo.discountedPrice);
      if (!isNaN(original) && !isNaN(discounted) && original > 0) {
        const savingsPercent = Math.round(
          ((original - discounted) / original) * 100
        );
        return `${savingsPercent}% off`;
      }
    }

    return "Discount applied";
  };

  return (
    <Container>
      {showTitle && <Title>Discount Code</Title>}

      {discountInfo ? (
        <AppliedCode>
          <div style={{ display: "flex", alignItems: "center" }}>
            <CheckCircleOutlineIcon
              style={{ color: "#28a745", fontSize: 18, marginRight: 8 }}
            />
            <div>
              <span style={{ fontWeight: "bold" }}>{discountInfo.code}</span>
              <span style={{ marginLeft: 8, color: "#28a745" }}>
                {getDiscountText()}
              </span>
            </div>
          </div>
          <ClearButton onClick={removeAppliedCode}>
            <CloseIcon style={{ color: "#565556", fontSize: 18 }} />
          </ClearButton>
        </AppliedCode>
      ) : (
        <>
          <InputWrapper>
            {/* H13 (a11y-i18n-mobileweb): the field's only visible name was a
                sibling <Title> div (not a <label>) + placeholder — invisible to
                the a11y name computation. Give it an explicit accessible name,
                and associate the error via aria-invalid + aria-describedby. */}
            <InputField
              type="text"
              value={discountCode}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={loading}
              aria-label="Discount code"
              aria-invalid={error ? "true" : undefined}
              aria-describedby={error ? "discount-code-error" : undefined}
            />
            {discountCode && (
              <ClearButton onClick={clearInput}>
                <CloseIcon style={{ color: "#565556", fontSize: 18 }} />
              </ClearButton>
            )}
            <ApplyButton
              onClick={() => applyCode(discountCode)}
              disabled={!discountCode.trim() || loading}
            >
              {loading ? <CircularProgress size={16} /> : "Apply"}
            </ApplyButton>
          </InputWrapper>
          {error && (
            <ErrorMessage id="discount-code-error" role="alert">
              {error}
            </ErrorMessage>
          )}
        </>
      )}
    </Container>
  );
};

DiscountCodeComponent.propTypes = {
  onApplyCode: PropTypes.func,
  initialCode: PropTypes.string,
  placeholder: PropTypes.string,
  showTitle: PropTypes.bool,
  error: PropTypes.string,
  loading: PropTypes.bool,
  discountInfo: PropTypes.shape({
    code: PropTypes.string,
    discountedPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    originalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    percentOff: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

export default DiscountCodeComponent;
